/**
 * What the on-screen keyboard is doing to the window, as four custom properties.
 *
 * `--kb` is what it covers, and everything that has to stay clear of it reads
 * that one number: the page's bottom padding, which is the room it scrolls
 * into; the dock's offset and the sheet's.
 *
 * `--app-h` is what is left of the window with the keyboard up, and the sheet
 * is capped against it rather than against the window.
 *
 * `--kb-up` is the same keyboard asked a different question - whether it is up
 * at all, and how tall - and the bottom safe area is the one thing that wants
 * it. What that inset keeps clear of is the home indicator, which is behind the
 * keys whether they cover the window or shortened it; on the client that
 * shortens it there is nothing covered and `--kb` would say there is no
 * keyboard to keep clear of.
 *
 * `--kb-glide` is not a measurement at all: it is how long the two bars that
 * stand on `--kb` may take to follow it, and it is written from here because
 * the answer is nought on any write in which the window itself changed height.
 *
 * Why any of this. The keyboard does not shrink the layout viewport on iOS, so
 * the page keeps its full height and its lower half stands behind the keys.
 * With the focused field down there and nothing below the last card to scroll
 * into, the client reveals it the only way left: it slides the whole layout
 * viewport up. That slide is what the app looked like it was doing to itself -
 * the screen flew, a strip of nothing appeared under it, and the field ended up
 * against the top edge with the title gone. Worse inside a sheet, which is
 * fixed and cannot be scrolled to at all: iOS does not repaint what is fixed
 * while the page is slid, so the form came out half drawn and stayed that way
 * until the screen was touched.
 *
 * Two answers, and the second is the one that matters. The page gets somewhere
 * to scroll, so the slide is not needed - and the numbers arrive on the focus
 * rather than on the keyboard, so they are already in place when the client
 * decides what to do.
 */

export type Metrics = {
  /** The layout viewport - `window.innerHeight`, which the keyboard leaves alone. */
  innerHeight: number;
  /** The visual viewport: what is left of the window once the keyboard is up. */
  height: number;
  /** How far the visual viewport has slid down inside the layout one. */
  offsetTop: number;
  /** Pinch, and 1 when there is none. */
  scale: number;
};

/** The properties, as they go into the stylesheet. */
export type Vars = Record<string, string>;

/**
 * Measure.
 *
 * Multiplied by the scale, because the visual viewport is measured in its own
 * pixels and the app is laid out in the page's: two fingers on the screen halve
 * `height` without a keyboard having appeared, and an app that believed that
 * number would fold itself to half a screen for as long as somebody was looking
 * closely at something. `offsetTop` is already in the page's pixels and is left
 * alone.
 *
 * Rounded in the direction that cannot overlap: the app's box is floored, so it
 * never claims a pixel that is under the keyboard, and the keyboard is ceilinged
 * for the same reason from the other side. A device with a fractional scale
 * hands over fractional heights, and the two rounding the same way would leave
 * the dock half a pixel into the keyboard.
 */
export function viewportVars(metrics: Metrics): Vars {
  const visible = metrics.height * metrics.scale;
  const covered = metrics.innerHeight - visible - metrics.offsetTop;
  return {
    '--app-h': `${Math.floor(visible)}px`,
    '--kb': `${Math.max(0, Math.ceil(covered))}px`,
  };
}

/**
 * How long the sheet and the dock take to follow `--kb`.
 *
 * The length of a keyboard's own arrival, so the two halves are glued rather
 * than raced. Written from here rather than declared once in the stylesheet
 * because it is not always this: see `put` below for the write it has to be
 * nought on.
 */
export const KB_GLIDE_MS = 300;

/**
 * The window as it will be once a keyboard of this height is up.
 *
 * Written on the focus rather than on the keyboard: by the time the client is
 * deciding how to reveal the field, the page is already as long as it needs to
 * be and the sheet already stands where the keys are about to stop. Nothing has
 * to be slid, so nothing is.
 */
export function anticipatedVars(innerHeight: number, keyboard: number): Vars {
  return {
    '--app-h': `${Math.max(0, innerHeight - keyboard)}px`,
    '--kb': `${keyboard}px`,
  };
}

/**
 * Whether focusing this is what brings a keyboard up.
 *
 * A date or a time brings a wheel of a different height, a button brings
 * nothing, and guessing a keyboard for either would leave the app short of a
 * screen until the guess ran out. Text, and the flavours of text.
 */
export function types(target: unknown): boolean {
  const element = target as { tagName?: string; type?: string; isContentEditable?: boolean } | null;
  if (element === null || element === undefined) return false;
  if (element.isContentEditable === true) return true;
  const tag = element.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag !== 'INPUT') return false;
  return TYPED.includes((element.type ?? 'text').toLowerCase());
}

const TYPED = ['text', 'search', 'url', 'tel', 'email', 'password', 'number', ''];

/**
 * How long a guessed keyboard is allowed to stand before the measurement it was
 * standing in for. Generous: what it protects against is a field that turned
 * out not to open a keyboard at all, and being wrong for a fifth of a second is
 * cheaper than dropping the guess while the keys are still on their way up.
 */
export const ANTICIPATION_MS = 900;

/** Where the last keyboard's height is kept, by the window it was measured in. */
const REMEMBERED = 'antifon:keyboard';

/**
 * Where «this client shrinks the window itself» is kept, once seen.
 *
 * Read off the owner's phone on 2026-08-24, from the readout: `ih 852 → 517`
 * with `vh` following it exactly and `ot 0` throughout. Telegram's iOS webview
 * does not do what Safari does. It does not leave the layout viewport at full
 * height with the keys over it - it hands the app a window that is already the
 * right size, and `visualViewport` then agrees with `innerHeight` at every
 * moment. There is nothing covered, and so nothing for `--kb` to be.
 *
 * Everything above this line is for the other kind of client, and on this one it
 * is not merely unnecessary but harmful: the guess lifts the sheet by a keyboard
 * that is not there, over a window that has already made room for it. That is
 * «дриблинг» - a form that jumps 78px on a tap and comes back a second later,
 * which is what the recording finally showed.
 *
 * Kept in storage rather than worked out each time: the first focus of a
 * session is the one that would otherwise guess wrong, and it is the one a
 * person notices.
 */
const SHRINKS = 'antifon:shrinks';

/** Where that client's shrink is kept, by the window it was measured in. */
const SHRUNK = 'antifon:shrink';

/**
 * What to guess when this window has never had a keyboard in it.
 *
 * The first field somebody ever touches is the one this is for, and after it
 * the measurement is remembered. Deliberately a shade more than an iPhone's
 * keyboard, which is about 0.41 of the screen with its suggestion strip: too
 * much leaves the dock standing a little high for the fifth of a second before
 * the measurement lands, too little leaves the page short of the room it is
 * being given and the client slides it anyway - which is the whole thing this
 * is here to prevent.
 */
const TYPICAL_SHARE = 0.45;

/**
 * When to look at the viewport after a focus, in milliseconds.
 *
 * Close together while the keys are travelling and thinning out after: the
 * keyboard is up inside about 350ms, the client may or may not say so, and the
 * last two are there for a client that animates slowly or reports late. Ending
 * a little after `ANTICIPATION_MS`, so the guess is never the last word.
 */
const PROBES = [80, 160, 260, 400, 600, 850, 1200, 1600];

type ViewportEvents = {
  height: number;
  offsetTop: number;
  scale: number;
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
};

export type ViewportSource = {
  innerWidth: number;
  innerHeight: number;
  visualViewport: ViewportEvents | null;
  requestAnimationFrame: (callback: () => void) => unknown;
  setTimeout: (callback: () => void, ms: number) => number;
  clearTimeout: (id: number) => void;
  matchMedia: (query: string) => { matches: boolean };
  localStorage: { getItem: (key: string) => string | null; setItem: (key: string, value: string) => void };
  document: {
    addEventListener: (type: string, listener: (event: { target: unknown }) => void) => void;
    removeEventListener: (type: string, listener: (event: { target: unknown }) => void) => void;
  };
};

/**
 * Follow the viewport for as long as the app is open.
 *
 * `scroll` as well as `resize`: with the keyboard up the visual viewport can
 * slide inside the layout one, and only the scroll event says so.
 *
 * Both are answered a frame later and at most once per frame. The client fires
 * them by the frame while the keyboard animates in, and a property written on
 * every one of them moves the dock and the sheet on every one of them - which
 * is the shaking that came with the first report.
 *
 * And measured on a schedule of its own after a focus, because the client is
 * not obliged to report anything. That is the second report, 2026-08-24: a form
 * opened over a keyboard stood at the top of the screen and dropped onto the
 * keys the moment the screen was touched. A touch is a scroll, a scroll is an
 * event, and the event is what the app had been waiting for - so the numbers
 * were right all along and arrived only when somebody prodded the client into
 * saying them. `PROBES` asks instead.
 *
 * Silent where there is no `visualViewport` at all: the properties keep the
 * fallbacks the stylesheet declares, which is the app as it was before any of
 * this - a full-height window and no keyboard inset.
 */
export function followViewport(
  source: ViewportSource = window,
  write: (name: string, value: string) => void = (name, value) =>
    document.documentElement.style.setProperty(name, value),
): () => void {
  const viewport = source.visualViewport;
  if (viewport === null) return () => {};

  let written: string | null = null;
  let queued = false;
  /**
   * While a keyboard is on its way up, the tallest strip seen since the focus -
   * starting at what was guessed. `--kb` is written from this rather than from
   * the raw measurement, so it only ever grows while the keys travel.
   *
   * This is the whole of the third report, 2026-08-24. The client animates the
   * keyboard and reports the viewport by the frame, so the strip it covers goes
   * 1, 7, 16, 25 … 336 over a third of a second - and the sheet, which stands
   * on `--kb`, rode every one of those numbers. Worse than a slow arrival: the
   * guess put the sheet up at once, the first frame of the animation said «1px
   * covered», and the sheet dropped the whole way back down before climbing
   * again. Measured on the rig: thirty-five moves, one of them 304px downwards.
   */
  let floor: number | null = null;
  /** The window the flight started in, so a window that changes ends it. */
  let window0 = source.innerHeight;
  /** The window as it was at the last focus, for spotting a client that shrinks it. */
  let before = source.innerHeight;
  /**
   * The window as it was before this client shortened it for its own keyboard,
   * for as long as it is short. Null on every other client and at rest.
   */
  let full: number | null = null;
  /**
   * The window as it was at the last write, for telling a keyboard arriving
   * from the window itself changing size. See `put`.
   */
  let lastWindow = source.innerHeight;
  let expiry: number | null = null;
  /** The measurements asked for after a focus, still to come. */
  let probes: number[] = [];

  const put = (vars: Vars): void => {
    // `--kb` is what the keyboard covers, and on a client that shortens the
    // window instead it covers nothing - so a bar that reads it stands 34px
    // clear of a home indicator that is behind the keys. `--kb-up` is the
    // keyboard however it arrived, and the bottom inset is the one thing that
    // wants that question rather than «how much is hidden».
    // And how long what stands on `--kb` may take to get there, which is nought
    // for a write in which the window itself changed height.
    //
    // The client that shortens the window for its own keyboard is the case this
    // exists for. There `--kb` holds the shrink that is about to happen and then
    // falls to nought in the very frame the window gets shorter - the same place
    // on the screen twice, with nothing in between, which is the whole of what
    // was built on 2026-08-27. A glide over that pair would be a 300ms ride
    // downwards to a place the sheet is already standing in. Every other window
    // change - a rotation, the client going fullscreen - is measured against a
    // window that no longer exists and is no better to animate.
    const settled = source.innerHeight === lastWindow;
    lastWindow = source.innerHeight;
    const all: Vars = {
      ...vars,
      // The window these numbers were measured in, so that the stylesheet can
      // tell an anticipation that has come true from one that is still owed.
      // `--kb` holds a shrink that has not happened yet; the frame it happens,
      // the difference between this and `100dvh` is exactly what has stopped
      // being covered, and `--kb-now` subtracts it without waiting for a
      // listener to run. See «--kb-now» in index.css for the frame that cost.
      '--kb-win': `${source.innerHeight}px`,
      '--kb-up': full === null ? (vars['--kb'] ?? '0px') : `${full - source.innerHeight}px`,
      '--kb-glide': settled ? `${KB_GLIDE_MS}ms` : '0ms',
    };
    const stamp = Object.values(all).join(' ');
    // Nothing has moved, so nothing is written: a style write on the root
    // element invalidates layout for the whole app, and the scroll event fires
    // on plain scrolling too, where none of the numbers has changed.
    if (stamp === written) return;
    written = stamp;
    for (const [name, value] of Object.entries(all)) write(name, value);
  };

  const forget = (): void => {
    floor = null;
    if (expiry !== null) {
      source.clearTimeout(expiry);
      expiry = null;
    }
  };

  const stopProbing = (): void => {
    for (const probe of probes) source.clearTimeout(probe);
    probes = [];
  };

  /**
   * Measure a few times over the next second and a half, whatever the client
   * says or does not say.
   *
   * The keyboard takes about a third of a second to arrive and the client may
   * report the viewport once, late, or never - and where it never does, the app
   * stands with a sheet at the top of the screen until somebody touches it. The
   * probes cost seven reads of two numbers and stop by themselves; `put` writes
   * nothing when nothing has moved, so the ones that land on an unchanged
   * viewport are free.
   */
  const probe = (): void => {
    stopProbing();
    probes = PROBES.map((delay) => source.setTimeout(() => apply(), delay));
  };

  /** What is covered right now, by the same arithmetic `viewportVars` uses. */
  const covering = (): number =>
    source.innerHeight - viewport.height * viewport.scale - viewport.offsetTop;

  const apply = (): void => {
    // A window smaller than it was at the focus is a client that makes room for
    // its own keyboard. Learned once and then remembered, because the focus it
    // would otherwise guess wrong on is the first one of a session.
    if (source.innerHeight < before) {
      learnShrinking(source);
      // And by how much, keyed by the window it happened in. Nothing else on
      // this client ever measures a keyboard - there is nothing covered to
      // measure - so without this the next focus has nothing to hold and the
      // half second before the client shrinks is a bar behind the keys.
      rememberShrink(source, before, before - source.innerHeight);
      full = before;
      before = source.innerHeight;
    } else if (full !== null && source.innerHeight >= full) {
      // The keys are gone and the window is whole again.
      full = null;
    }
    const vars = viewportVars({
      innerHeight: source.innerHeight,
      height: viewport.height,
      offsetTop: viewport.offsetTop,
      scale: viewport.scale,
    });
    const covered = Number.parseInt(vars['--kb'] ?? '0', 10);
    if (floor !== null) {
      // The keys are travelling, and `--kb` may only grow while they do. Every
      // frame of that animation is a number the client reports and the sheet
      // stands on, so a strip that follows them exactly is a sheet that climbs
      // in thirty steps - and one that believes the first frame («1px covered»)
      // drops the whole way down before it starts.
      //
      // «They have stopped» is deliberately not decided here. It was, for one
      // measurement, on «the same number twice in a row» - and a probe landing
      // in the same frame as a resize says that about a keyboard still halfway
      // up. The window is what ends the flight instead: the guess stands for
      // ANTICIPATION_MS and the measurement lands once, when it is over.
      //
      // The floor rises only for a strip that could be a keyboard at all. This
      // is the correction of the same evening, read off a screen recording:
      // «встаёт на место, потом куда-то вверх летает, потом опять на место»,
      // about a third of a second high. A single frame reporting something
      // absurd - a client mid-transition, the window changing height under a
      // fullscreen switch - used to move the sheet for one frame; with a floor
      // that only grows it moved it for the whole window. The band that already
      // guards what gets remembered guards what gets drawn now too.
      if (covered > floor && plausible(covered, source.innerHeight)) floor = covered;
      // The window itself changing size is not a keyboard arriving, and every
      // number in flight was measured against the old one. Start again from
      // what is actually there.
      if (source.innerHeight !== window0) {
        forget();
        if (covered > 0) remember(source, covered);
        put(vars);
        return;
      }
      if (covered > 0) remember(source, covered);
      put(anticipatedVars(source.innerHeight, floor));
      return;
    }
    if (covered > 0) remember(source, covered);
    put(vars);
  };

  const schedule = (): void => {
    if (queued) return;
    queued = true;
    source.requestAnimationFrame(() => {
      queued = false;
      apply();
    });
  };

  /**
   * A frame later is right for everything except the window changing size, and
   * that one has to be answered in the frame it happens.
   *
   * Every number the app is standing on was measured against a window that has
   * just stopped existing. On the client that shortens the window for its own
   * keyboard this is not a subtlety: `--kb` is holding the 336px the window is
   * about to lose, and the frame the window loses them is the frame `--kb` has
   * to be nought. A frame of delay puts the sheet 336px up inside a window that
   * is already 336px shorter - its top a third of a screen above the top edge,
   * and the screen it was opened from showing in the band underneath.
   *
   * That is one frame, 33ms, and it is what the owner's recording of
   * 2026-09-02 caught at +430ms after the tap - the moment the client shrinks.
   * Reproduced on the rig by seeding the shrink into storage and resizing the
   * window under the app: top 48 → -288 → 48 over three frames, with the
   * sheet's height not moving at all.
   *
   * The batching itself stays. What it was written against is a client that
   * reports the viewport by the frame while the keys travel, and a window that
   * changes height is not that: it happens once on the way in and once on the
   * way out.
   */
  const resized = (): void => {
    if (source.innerHeight !== lastWindow) {
      apply();
      return;
    }
    schedule();
  };

  /**
   * Hold the window as it will be once the keys are up, until the client says
   * otherwise: the guess stands for `ANTICIPATION_MS`, and a window that
   * changes height ends it sooner because every number in it was measured
   * against a window that no longer exists.
   */
  const fly = (keyboard: number): void => {
    floor = keyboard;
    window0 = source.innerHeight;
    before = source.innerHeight;
    expiry = source.setTimeout(() => {
      expiry = null;
      floor = null;
      apply();
    }, ANTICIPATION_MS);
    put(anticipatedVars(source.innerHeight, keyboard));
    probe();
  };

  const onFocusIn = (event: { target: unknown }): void => {
    if (floor !== null || !types(event.target)) return;
    // This client shrinks the window itself, so nothing here is covered and
    // `--kb` will end at nought - but it does not shrink it until the keys are
    // nearly up. Measured on the owner's phone: the window was still 852 at
    // +68ms and became 517 at +497. For that half second everything pinned to
    // the bottom - the dock's button, the sheet - stands at the bottom of a
    // window whose lower third is behind the keyboard, and then arrives above
    // the keys when the client gets round to shrinking. That is the jump the
    // recording of «Создать хор» shows, 2026-08-27.
    //
    // So the shrink is anticipated exactly as a cover is: `--kb` holds the
    // height the window is about to lose, which puts the bar where the shrunken
    // window will put it, and drops to nought in the same frame the window
    // shrinks - the same place on the screen, twice, with nothing in between.
    //
    // Only from a window that has a shrink remembered for its own size, and
    // that is what keeps 2026-08-24's «guess nothing on this client» where it
    // matters: the shrunken window has no entry of its own, so a focus that
    // moves between fields with the keys already up guesses nothing, holds
    // nothing, and cannot lift a form over a window that will never shrink.
    if (shrinks(source)) {
      const shrink = recallShrink(source);
      if (shrink !== null) {
        fly(shrink);
        return;
      }
      before = source.innerHeight;
      probe();
      return;
    }
    // A coarse pointer is a phone or a tablet, and those are the two where a
    // focus brings a keyboard with it. On a desktop client the field is focused
    // and nothing else happens, and an app that shrank for it would be a screen
    // folded in half for no reason at all.
    if (!source.matchMedia('(pointer: coarse)').matches) return;
    // The keyboard is already up: the app box is right, and the client moves
    // between fields without changing anything about it. Measured against the
    // band rather than against zero - in a fullscreen client the two viewports
    // need not agree to the pixel at rest, and «anything covered at all» would
    // read a rounding difference as a keyboard and skip the guess for ever.
    if (plausible(covering(), source.innerHeight)) {
      probe();
      return;
    }
    fly(recall(source) ?? Math.round(source.innerHeight * TYPICAL_SHARE));
  };

  viewport.addEventListener('resize', resized);
  viewport.addEventListener('scroll', schedule);
  source.document.addEventListener('focusin', onFocusIn);
  // The first measurement is taken now rather than a frame from now: the app is
  // being laid out for the first time, and a frame of a wrong height is a frame
  // of the whole screen in the wrong place.
  apply();

  return () => {
    viewport.removeEventListener('resize', resized);
    viewport.removeEventListener('scroll', schedule);
    source.document.removeEventListener('focusin', onFocusIn);
    forget();
    stopProbing();
  };
}

/**
 * The keyboard this window had last time, if it is the same window.
 *
 * Kept across launches rather than only across screens, because the first field
 * of a session is the one somebody notices: a singer opens the app to type a
 * code and closes it again. Keyed by the window's own size, so a rotation or a
 * different phone is a different memory rather than a wrong one.
 */
function recall(source: ViewportSource): number | null {
  try {
    const raw = source.localStorage.getItem(key(source));
    if (raw === null) return null;
    const height = Number.parseInt(raw, 10);
    if (!Number.isFinite(height) || height <= 0) return null;
    // Checked again on the way out, not only on the way in: an entry written by
    // a version of this file that had no band is still in somebody's storage.
    return plausible(height, source.innerHeight) ? height : null;
  } catch {
    // Storage can be denied outright in a webview. Then every focus is the
    // first one, which is the behaviour this whole memory improves on.
    return null;
  }
}

/** Whether this client is known to shrink the window when the keyboard comes up. */
function shrinks(source: ViewportSource): boolean {
  try {
    return source.localStorage.getItem(SHRINKS) === '1';
  } catch {
    return false;
  }
}

/**
 * How much this client takes off the window for its own keyboard, by the window
 * it took it off.
 *
 * Kept apart from what `remember` keeps, and not by tidiness. What that one
 * holds is a *covered* strip, and on this client a covered strip is always an
 * artifact: 78px of one transitional frame got stored that way on 2026-08-24
 * and sent every later tap into a 78px lift. Reading it here would be reading
 * that back. A shrink is the client's own arithmetic and cannot be a fraction
 * of a transition.
 *
 * Keyed by the height the window had *before* it shrank, so the entry exists
 * only for the full window: in the shrunken one there is nothing to find, which
 * is how a focus with the keys already up knows to leave everything alone.
 */
function rememberShrink(source: ViewportSource, from: number, by: number): void {
  // The same band as everywhere else here, and for the same reason: a client
  // animating itself open or switching to fullscreen shrinks the window too,
  // and one of those stored as «the keyboard» lifts the next form by it.
  if (!plausible(by, from)) return;
  try {
    source.localStorage.setItem(shrinkKey(source.innerWidth, from), `${by}`);
  } catch {
    // Storage is optional here as everywhere else: without it every focus is
    // the first one, which is the behaviour this improves on.
  }
}

/** The shrink this window has been seen to make, if it is the full-sized one. */
function recallShrink(source: ViewportSource): number | null {
  try {
    const raw = source.localStorage.getItem(shrinkKey(source.innerWidth, source.innerHeight));
    if (raw === null) return null;
    const by = Number.parseInt(raw, 10);
    if (!Number.isFinite(by) || by <= 0) return null;
    return plausible(by, source.innerHeight) ? by : null;
  } catch {
    return null;
  }
}

function learnShrinking(source: ViewportSource): void {
  try {
    source.localStorage.setItem(SHRINKS, '1');
  } catch {
    // As everywhere else here: storage is optional, and without it the client
    // is worked out afresh on every focus, which costs one wrong guess each.
  }
}

function remember(source: ViewportSource, keyboard: number): void {
  // Not everything that covers the viewport is a keyboard, and what gets
  // remembered here is what the next focus lifts the sheet by before any
  // keyboard has arrived. A client animating the app open, a drag towards
  // closing it, a fullscreen transition measured half-way through: each is a
  // frame in which most of the window is «covered», and one of them stored as
  // «the keyboard» sends the next form somebody opens to the top of the screen.
  // The band is wide on purpose - it is there to refuse a nonsense, not to
  // second-guess a phone.
  if (!plausible(keyboard, source.innerHeight)) return;
  try {
    source.localStorage.setItem(key(source), `${keyboard}`);
  } catch {
    // As above: nothing is lost that was not already optional.
  }
}

/** Whether a covered strip this tall could be a keyboard at all. */
export function plausible(keyboard: number, innerHeight: number): boolean {
  return keyboard >= innerHeight * LEAST_SHARE && keyboard <= innerHeight * MOST_SHARE;
}

const LEAST_SHARE = 0.15;
const MOST_SHARE = 0.6;

function key(source: ViewportSource): string {
  return `${REMEMBERED}:${source.innerWidth}x${source.innerHeight}`;
}

function shrinkKey(width: number, height: number): string {
  return `${SHRUNK}:${width}x${height}`;
}
